import { useEffect, useState } from 'react';
import axios from 'axios';
import { AppointmentForm } from '@/components/AppointmentForm';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { SettingsMenu } from '@/components/SettingsMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const [googleAuthStatus, setGoogleAuthStatus] = useState({ is_authenticated: false });
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkGoogleAuth();
  }, []);

  const checkGoogleAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/status`);
      setGoogleAuthStatus(response.data);
    } catch (error) {
      console.error('Error checking Google auth:', error);
      setGoogleAuthStatus({ is_authenticated: false });
    } finally {
      setAuthChecked(true);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Fehler beim Google Login');
    }
  };

  const handleGoogleDisconnect = async () => {
    try {
      await axios.post(`${API}/auth/google/disconnect`);
      setGoogleAuthStatus({ is_authenticated: false });
      toast.success('Google Calendar getrennt');
    } catch (error) {
      toast.error('Fehler beim Trennen');
    }
  };

  const handleCreateAppointment = async (data) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/appointments`, data);
      toast.success('Termin erfolgreich erstellt und bestätigt!');
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Fehler beim Erstellen des Termins');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 h-14 bg-background/95 backdrop-blur-xl border-b border-border/50 px-4" data-testid="dashboard-header">
        <div className="h-full flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-heading text-primary tracking-tight" data-testid="dashboard-title">
              STRONG CHIPTUNING
            </h1>
            <p className="text-xs text-muted-foreground font-subheading">Terminverwaltung</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => window.location.href = '/termine'}
              variant="outline"
              size="sm"
              className="border-primary/50 text-primary hover:bg-primary/10 text-xs"
              data-testid="meine-termine-button"
            >
              Termine
            </Button>
            {!googleAuthStatus.is_authenticated ? (
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                size="sm"
                className="border-success/50 text-success hover:bg-success/10 text-xs"
                data-testid="google-auth-button"
              >
                Kalender
              </Button>
            ) : (
              <Button
                onClick={handleGoogleDisconnect}
                variant="outline"
                size="sm"
                className="border-success/50 text-success hover:bg-success/10 text-xs flex items-center gap-1"
                data-testid="google-disconnect-button"
              >
                <CheckCircle className="w-3 h-3" />
                <span>Verbunden</span>
              </Button>
            )}
            <SettingsMenu />
          </div>
        </div>
      </header>

      {/* Google Calendar Warning Banner */}
      {authChecked && !googleAuthStatus.is_authenticated && (
        <div className="mx-4 mt-3" data-testid="google-warning-banner">
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive font-medium">
                Google Calendar nicht verbunden!
              </p>
            </div>
            <Button
              onClick={handleGoogleLogin}
              size="sm"
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs flex-shrink-0"
              data-testid="google-reconnect-button"
            >
              Verbinden
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4" data-testid="dashboard-main">
        <div className="max-w-2xl mx-auto">
          {/* Create Appointment Form */}
          <section className="bg-card border border-border rounded-lg p-4">
            <h2 className="text-lg font-bold font-heading mb-4">Neuen Termin erstellen</h2>
            <AppointmentForm onSubmit={handleCreateAppointment} isLoading={isLoading} />
          </section>
        </div>
      </main>
    </div>
  );
}